import React, { useState, useEffect } from "react";
import { Button, Grid, Dropdown } from "semantic-ui-react";
import { Form, Input, Checkbox } from "formik-semantic-ui";
import { loadYdkCustomers } from "../actions";

import { ProductPriceTierSelector } from "../../product/price-tier/components/selector";

const CustomerComponentEditorForm = ({
    values,
    handleReset,
    hasFintech = false,
    productPriceTiers = [],
    hasTieredPricingEnabled = false,
    setFieldTouched,
    setFieldValue,
    onAddNewTier,
}) => {
    const [isFintechEnabled, setIsFintechEnabled] = useState(!!values?.fintech_option);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [ignore, setIgnore] = useState(true);

    useEffect(() => {
        if (!values?.aims_license_type || !values?.aims_id) {
            return;
        }

        let isMounted = true;
        loadYdkCustomers(values.aims_license_type, values.aims_id).then(customers => {
            if (!isMounted) {
                return;
            }

            const options = (customers || []).map(item => ({
                key: item.establishment_id,
                text: item.name,
                content: (
                    <>
                        <strong>{item.name}</strong> <br />
                        {item.address}, {item.city}, {item.state}, {item.zip}
                    </>
                ),
                value: item.establishment_id,
            }));

            setCustomerOptions(options);

            if (ignore) {
                setIgnore(false);
                return;
            }

            setFieldValue("ydk_est_id", 0);
        });

        return () => {
            isMounted = false;
        };
    }, [values?.aims_license_type, values?.aims_id]);

    return (
        <Grid divided>
            <Grid.Row>
                <Grid.Column mobile={16}>
                    <Form.Group widths="equal">
                        <Input
                            name="aims_license_type"
                            label="AIMS License Type *"
                            inputProps={{
                                placeholder: "MB,BG etc.",
                                "data-cy": "aims_license_type",
                            }}
                        />
                        <Input
                            name="aims_id"
                            label="AIMS ID *"
                            inputProps={{
                                placeholder: "1234...",
                                "data-cy": "aims_id",
                            }}
                        />

                        <Input
                            name="name"
                            label="Name *"
                            inputProps={{
                                placeholder: "Customer Name",
                                "data-cy": "name",
                            }}
                        />
                    </Form.Group>

                    <Form.Group widths="equal">
                        <Input
                            name="address"
                            label="Address *"
                            inputProps={{
                                placeholder: "123 Main st",
                                "data-cy": "address",
                            }}
                        />
                        <Input
                            name="city"
                            label="City *"
                            inputProps={{
                                placeholder: "Austin",
                                "data-cy": "city",
                            }}
                        />
                        <Input
                            name="state"
                            label="State *"
                            inputProps={{
                                placeholder: "TX",
                                "data-cy": "state",
                            }}
                        />
                        <Input
                            name="zip"
                            label="Zip *"
                            inputProps={{
                                placeholder: "78078",
                                "data-cy": "zip",
                            }}
                        />
                    </Form.Group>

                    <Form.Group widths="equal">
                        <Input
                            name="contact_name"
                            label="Contact Name"
                            inputProps={{
                                placeholder: "Full Name",
                                "data-cy": "contact_name",
                            }}
                        />
                        <Input
                            name="contact_email"
                            label="Contact Email"
                            inputProps={{
                                placeholder: "customer@email.com",
                                "data-cy": "email",
                            }}
                        />
                        <Input
                            name="contact_phone"
                            label="Contact Phone"
                            inputProps={{
                                placeholder: "Contact Phone",
                                "data-cy": "phone",
                            }}
                        />
                    </Form.Group>

                    <Form.Group widths="equal">
                        <Input
                            name="county"
                            label="County *"
                            inputProps={{
                                placeholder: "Harris",
                                "data-cy": "county",
                            }}
                        />
                        <Input
                            name="loyalty_id"
                            label="Loyalty ID *"
                            inputProps={{
                                placeholder: "Loyalty ID",
                                "data-cy": "loyalty_id",
                            }}
                        />
                        <Input
                            name="taxpayer"
                            label="Taxpayer # *"
                            inputProps={{
                                placeholder: "Taxpayer Number",
                                "data-cy": "taxpayer",
                            }}
                        />
                    </Form.Group>

                    <Form.Group widths={3}>
                        {hasTieredPricingEnabled && (
                            <div className="field">
                                <div className="label" style={{ marginBottom: "0.28rem" }}>
                                    Price Tier
                                </div>
                                <ProductPriceTierSelector
                                    search
                                    clearable={false}
                                    priceTiers={productPriceTiers}
                                    allowAdditions
                                    additionLabel="Create New Tier "
                                    onAddItem={onAddNewTier}
                                    value={parseInt(values?.product_price_tier_id)}
                                    onChange={(_, { value }) => {
                                        setFieldValue("product_price_tier_id", value);
                                        setFieldTouched("product_price_tier_id");
                                    }}
                                />
                            </div>
                        )}
                    </Form.Group>

                    <br />
                    <Form.Group widths={3}>
                        <Checkbox
                            data-cy="cash_on_delivery"
                            name="cash_on_delivery"
                            label="Cash On Delivery"
                            inputProps={{ toggle: true }}
                        />
                        {!!hasFintech && (
                            <>
                                <Checkbox
                                    type="checkbox"
                                    name="fintech_option"
                                    label="Fintech Option"
                                    inputProps={{
                                        toggle: true,
                                        onChange: () => setIsFintechEnabled(!isFintechEnabled),
                                    }}
                                />
                                {!!isFintechEnabled && (
                                    <Input
                                        name="fintech_id"
                                        label="Fintech ID *"
                                        inputProps={{
                                            placeholder: "Fintech ID",
                                            "data-cy": "fintech_id",
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </Form.Group>

                    {values.id && (
                        <>
                            <Form.Group widths={3}>
                                <div className="field" style={{ marginTop: "1rem" }}>
                                    <div
                                        className="label"
                                        style={{
                                            marginBottom: "0.28rem",
                                            fontSize: "0.92857143em",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        <b>Link to an YDK source customer</b>
                                    </div>
                                    <Dropdown
                                        clearable
                                        selection
                                        name="ydk_est_id"
                                        options={customerOptions}
                                        value={
                                            Number(values.ydk_est_id) === 0 ? "" : values.ydk_est_id
                                        }
                                        placeholder={
                                            customerOptions.length
                                                ? "Select Customer"
                                                : "No customers found for this TABC ID"
                                        }
                                        onChange={(_, { value }) => {
                                            setFieldValue("ydk_est_id", value || 0);
                                            setFieldTouched("ydk_est_id", true);
                                        }}
                                        onBlur={() => setFieldTouched("ydk_est_id", true)}
                                    />
                                </div>
                            </Form.Group>
                        </>
                    )}
                </Grid.Column>

                <Grid.Column width={16}>
                    <br />
                    <div style={{ marginBottom: "1rem", color: "#666", fontSize: "0.9em" }}>
                        Fields marked with (*) are required.
                    </div>
                    <Button data-cy="save_customer" color="teal" type="submit">
                        {values.id ? "Update" : "Create"} Customer
                    </Button>
                    <Button type="button" color="orange" onClick={() => handleReset()}>
                        Reset
                    </Button>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};

export default CustomerComponentEditorForm;
